import { fetchCorpOptions } from "@app/api/corp";
import { fetchAmazonVcWarehouseOptions } from "@app/api/warehouse/amazonVc";
import { fetchExternalWarehouseSelectOptions } from "@app/api/warehouse/productPairing";
import { toDfSellableListRequestParams, type DfSellableQueryParam } from "@app/views/order/availableStock/config/filter";
import { mapDfSellableListItem, type DfSellableListItemApi } from "@app/views/order/availableStock/config/list.data";
import { mapDfSellableOperateLogItem, type DfOperateLogItem, type DfSellableOperateLogApiItem } from "@app/views/order/availableStock/config/operateLog";
import { DEFAULT_PAGE_SIZE, type FetchListOptions, type ListResult } from "@shared/types/list";
import { http } from "@shared/utils/request";
import type { DfSellableProductRow } from "@app/views/order/availableStock/config/types";

/** 日志请求端：1=客户端，2=管理端 */
export type DfSellableOperateLogSide = 1 | 2;

export interface DfSellableFilterOptions {
    corpOptions: { label: string; value: string }[];
    /** 1P仓库：与产品配对一致，value=externalWarehouseId */
    warehouseNameOptions: { label: string; value: string }[];
    warehouseCodeOptions: { label: string; value: string }[];
}

/** storeList 单项内嵌的分区库存汇总（侧栏二级数量） */
export interface DfSellableZoneStockSummaryApi {
    total?: number;
    items?: Array<{ value?: string; label?: string; cnt?: number; count?: number }>;
}

/** DF 可售库存 — 店铺 Tab 项（POST /manage/amazonVcDfSellableStock/storeList） */
export interface DfSellableStoreListItem {
    storeId: string;
    storeName: string;
    vcCode: string;
    zoneStockSummary?: DfSellableZoneStockSummaryApi;
}

interface DfSellableStoreListApiItem {
    storeId: string;
    storeName?: string;
    name?: string;
    vcCode?: string;
    sellerId?: string;
    zoneStockSummary?: DfSellableZoneStockSummaryApi;
}

interface DfSellableStoreListApiData {
    items?: DfSellableStoreListApiItem[];
}

interface DfSellableListApiData {
    items?: DfSellableListItemApi[];
    total?: number;
}

/**
 * DF 可售库存列表
 * POST /manage/amazonVcDfSellableStock/list
 */
export async function fetchDfSellableList(query: DfSellableQueryParam, options?: FetchListOptions): Promise<ListResult<DfSellableProductRow>> {
    const data = await http.post<DfSellableListApiData>("/manage/amazonVcDfSellableStock/list", toDfSellableListRequestParams(query), {
        signal: options?.signal,
    });

    return {
        list: (data.items ?? []).map(mapDfSellableListItem),
        total: data.total ?? 0,
    };
}

/**
 * 修改 DF 可售库存 Y（仓码维度）
 * POST /manage/amazonVcDfSellableStock/updateStock
 */
export function updateDfSellableStock(params: {
    /** warehouseCodes[].id */
    id: string;
    platformStock: number;
}) {
    return http.post<null>("/manage/amazonVcDfSellableStock/updateStock", {
        id: String(params.id ?? "").trim(),
        platformStock: Number(params.platformStock) || 0,
    });
}

/** 批量修改 DF 可售库存：1 绝对值；2 百分比 */
export type DfSellableBatchUpdateStockMode = 1 | 2;

/**
 * 批量修改 DF 可售库存
 * POST /manage/amazonVcDfSellableStock/batchUpdateStock
 * @param ids warehouseCodes[].id（雪花 string）
 * @param updateMode 1 绝对值；2 百分比
 */
export function batchUpdateDfSellableStock(params: { ids: string[]; updateMode: DfSellableBatchUpdateStockMode; value: number }) {
    return http.post<null>("/manage/amazonVcDfSellableStock/batchUpdateStock", {
        ids: (params.ids || []).map(id => String(id ?? "").trim()).filter(Boolean),
        updateMode: params.updateMode === 2 ? 2 : 1,
        value: Number(params.value) || 0,
    });
}

/**
 * DF 可售库存导出
 * POST /manage/amazonVcDfSellableStock/export
 *
 * @param ids warehouseCodes[].id（雪花 string）
 * @param updateMode 1 绝对值；2 百分比
 * @param value 绝对值 Y 或 百分比 X
 */
export function exportDfSellableStock(params: { ids: string[]; updateMode: DfSellableBatchUpdateStockMode; value: number }) {
    return http.post<{ taskId: string; downloadTaskId?: string }>(
        "/manage/amazonVcDfSellableStock/export",
        {
            ids: (params.ids || []).map(id => String(id ?? "").trim()).filter(Boolean),
            updateMode: params.updateMode === 2 ? 2 : 1,
            value: Number(params.value) || 0,
        },
        // 与其它 export 一致：任务型导出走 asyncTask 轮询，这里不需要取消重复
        { cancelDuplicate: false },
    );
}

/** 设置规则 — comboStockMode：1 自身仓存；2 子品短板 */
export type DfSellableSetRulesComboStockMode = 1 | 2;

export interface DfSellableSetRulesParams {
    /** warehouseCodes[].warehouses[].id */
    id: string;
    warningStock: number;
    percent: number;
    comboStockMode: DfSellableSetRulesComboStockMode;
}

/** setSyncStatus / setRules / batchSetRules 等写操作可能返回的日志项 */
export interface DfSellableSetRulesLogItem {
    id?: string;
    createTimeLabel?: string;
    operatorName?: string;
    action?: number;
    actionLabel?: string;
    logType?: number;
    logTypeLabel?: string;
    detailLabel?: string;
    beforeStock?: number;
    afterStock?: number;
}

/**
 * 设置 DF 可售库存同步状态（暂停 / 恢复）
 * POST /manage/amazonVcDfSellableStock/setSyncStatus
 * @param id 库存 Y 记录 id（warehouseCodes[].id；产品维度用列表组代表 id）
 * @param syncStatus 1=正常同步，0=暂停同步
 */
export function setDfSellableSyncStatus(params: { id: string; syncStatus: 0 | 1 }) {
    return http.post<{ items?: DfSellableSetRulesLogItem[] }>("/manage/amazonVcDfSellableStock/setSyncStatus", {
        id: String(params.id ?? "").trim(),
        syncStatus: params.syncStatus === 1 ? 1 : 0,
    });
}

function toDfSellableSetRulesBody(params: DfSellableSetRulesParams) {
    return {
        id: String(params.id ?? "").trim(),
        warningStock: Number(params.warningStock) || 0,
        percent: Number(params.percent) || 0,
        comboStockMode: params.comboStockMode,
    };
}

/**
 * 设置 DF 可售库存规则
 * POST /manage/amazonVcDfSellableStock/setRules
 * @param productId warehouseCodes[].warehouses[].id
 */
export function setDfSellableRules(params: DfSellableSetRulesParams) {
    return http.post<{ items?: DfSellableSetRulesLogItem[] }>("/manage/amazonVcDfSellableStock/setRules", toDfSellableSetRulesBody(params));
}

export interface DfSellableBatchSetRulesParams {
    /** warehouseCodes[].warehouses[].id */
    ids: string[];
    warningStock: number;
    percent: number;
}

/**
 * 批量设置 DF 可售库存规则（多仓一次提交）
 * POST /manage/amazonVcDfSellableStock/batchSetRules
 * @param ids warehouseCodes[].warehouses[].id
 */
export function batchSetDfSellableRules(params: DfSellableBatchSetRulesParams) {
    return http.post<{ items?: DfSellableSetRulesLogItem[] }>("/manage/amazonVcDfSellableStock/batchSetRules", {
        ids: params.ids.map(id => String(id ?? "").trim()).filter(Boolean),
        warningStock: Number(params.warningStock) || 0,
        percent: Number(params.percent) || 0,
    });
}

/**
 * DF 可售库存操作日志
 * POST /manage/amazonVcDfSellableStock/logList
 */
export async function fetchDfSellableOperateLogList(params: {
    sellableStockId: string;
    /** 1=客户端，2=管理端；管理端默认 2 */
    side?: DfSellableOperateLogSide;
    page?: number;
    perPage?: number;
}): Promise<ListResult<DfOperateLogItem>> {
    const sellableStockId = String(params.sellableStockId ?? "").trim();
    if (!sellableStockId) return { list: [], total: 0 };

    const data = await http.post<{ items?: DfSellableOperateLogApiItem[]; total?: number }>("/manage/amazonVcDfSellableStock/logList", {
        sellableStockId,
        side: params.side ?? 2,
        page: params.page ?? 1,
        perPage: params.perPage ?? DEFAULT_PAGE_SIZE,
    });
    return {
        list: (data.items ?? []).map(mapDfSellableOperateLogItem),
        total: data.total ?? 0,
    };
}

/**
 * DF 可售库存店铺列表（侧栏 Tab + 分区数量）
 * POST /manage/amazonVcDfSellableStock/storeList
 * 二级统计取 items[].zoneStockSummary，不再另调 zoneStockSummary
 */
export async function fetchDfSellableStoreList(): Promise<{ items: DfSellableStoreListItem[] }> {
    const data = await http.post<DfSellableStoreListApiData>("/manage/amazonVcDfSellableStock/storeList", {});
    const items: DfSellableStoreListItem[] = [];
    for (const item of data.items ?? []) {
        const storeId = item.storeId;
        const storeName = String(item.storeName ?? item.name ?? "").trim();
        const vcCode = String(item.vcCode ?? item.sellerId ?? "").trim();
        if (!storeId || storeId === "0") continue;
        items.push({
            storeId,
            storeName,
            vcCode,
            zoneStockSummary: item.zoneStockSummary,
        });
    }
    return { items };
}

/**
 * 筛选项下拉：
 * - 企业名称 GET /manage/corp/options
 * - 1P仓库名称 POST /manage/warehouse/external/options（与产品配对一致）
 * - 仓库代码 POST /manage/warehouseAmazonVc/options（vcType=DF）
 */
export async function fetchDfSellableFilterOptions(): Promise<DfSellableFilterOptions> {
    const [corpRes, externalWarehouses, vcWarehouses] = await Promise.all([fetchCorpOptions({ cooperationStatus: 1 }), fetchExternalWarehouseSelectOptions(), fetchAmazonVcWarehouseOptions("DF")]);

    const corpOptions = (corpRes.items ?? []).map(item => ({
        label: item.corpName || item.corpCode || String(item.id),
        value: item.id,
    }));

    const codeSet = new Set<string>();
    const warehouseCodeOptions: { label: string; value: string }[] = [];
    for (const item of vcWarehouses) {
        const code = String(item.warehouseCode || "").trim();
        if (code && !codeSet.has(code)) {
            codeSet.add(code);
            warehouseCodeOptions.push({ label: code, value: code });
        }
    }

    return {
        corpOptions,
        warehouseNameOptions: externalWarehouses,
        warehouseCodeOptions,
    };
}
